import { useEffect, useRef } from 'react';
import { useSelectionStore, type SelectionSnapshot } from '@/state/selectionStore';

const MAX_DEPTH = 20;

/**
 * Selection-only undo. ⌘Z / Ctrl+Z reverts active-scene change + reference toggles.
 * Does NOT undo image generation, deletion, or prompt edits — those stay explicit.
 */
export function useSelectionUndo() {
  const stack = useRef<SelectionSnapshot[]>([]);
  const isRestoring = useRef(false);

  // Push a snapshot whenever activeSceneId or pendingReferences change (debounced via subscribe)
  useEffect(() => {
    const sub = useSelectionStore.subscribe((state, prev) => {
      if (isRestoring.current) {
        isRestoring.current = false;
        return;
      }
      const changed =
        state.activeSceneId !== prev.activeSceneId ||
        !sameRefs(state.pendingReferences, prev.pendingReferences) ||
        state.freshPrompt !== prev.freshPrompt;
      if (!changed) return;

      stack.current.push({
        activeSceneId: prev.activeSceneId,
        pendingReferences: {
          sceneIds: [...prev.pendingReferences.sceneIds],
          castIds: [...prev.pendingReferences.castIds],
        },
        freshPrompt: prev.freshPrompt,
      });
      if (stack.current.length > MAX_DEPTH) stack.current.shift();
    });
    return sub;
  }, []);

  // Keybinding
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod || e.key !== 'z' || e.shiftKey) return;

      // Ignore when typing in form fields — let the browser handle native undo
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        return;
      }

      const last = stack.current.pop();
      if (!last) return;
      e.preventDefault();
      isRestoring.current = true;
      useSelectionStore.getState().restore(last);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

function sameRefs(
  a: { sceneIds: string[]; castIds: string[] },
  b: { sceneIds: string[]; castIds: string[] },
): boolean {
  return (
    a.sceneIds.length === b.sceneIds.length &&
    a.castIds.length === b.castIds.length &&
    a.sceneIds.every((x, i) => x === b.sceneIds[i]) &&
    a.castIds.every((x, i) => x === b.castIds[i])
  );
}
